import type { UsageAppMixEntry } from "@/v2-backoffice-client/usage/UsageAppMix"
import type { ChurnRiskLevel } from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  MOVER_ACTIVITY_FLOOR_EVENTS,
  MOVER_LIMIT,
  PORTFOLIO_SEGMENT_ORDER,
  type PortfolioMixState,
  type PortfolioSegmentKey,
} from "@/v2-backoffice-client/portfolio/portfolio.constants"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioRiskCounts = Record<
  ChurnRiskLevel,
  {
    count: number
    /** 12-month billed revenue riding on the level's customers. */
    billed12M: number
  }
>

/**
 * Customers per churn-risk level with the 12-month billed revenue behind
 * each level, for the risk breakdown — the level says who might leave, the
 * money says what that would cost.
 */
export function derivePortfolioRiskCounts(
  customers: PortfolioCustomerSummary[],
): PortfolioRiskCounts {
  const counts: PortfolioRiskCounts = {
    high: { count: 0, billed12M: 0 },
    medium: { count: 0, billed12M: 0 },
    low: { count: 0, billed12M: 0 },
    new: { count: 0, billed12M: 0 },
    none: { count: 0, billed12M: 0 },
  }
  for (const customer of customers) {
    const entry = counts[customer.summary.churnRisk.level]
    entry.count += 1
    entry.billed12M += customer.billed12M ?? 0
  }
  return counts
}

export type PortfolioRevenueAtRisk = {
  /** 12-month billed revenue on high + medium churn-risk customers. */
  atRisk: number
  /** Of that, the high-risk share. */
  high: number
  /** 12-month billed revenue across every customer in scope. */
  total: number
  /** Display label; every live Fenerum invoice is NOK today. */
  currency: string | null
}

/**
 * The money the current churn signals put at stake: the 12-month billed
 * revenue of at-risk (high + medium) customers against the whole scoped
 * book. Sums are nominal across currencies (single-currency reality today —
 * see lic.CustomerRevenueFacts).
 */
export function derivePortfolioRevenueAtRisk(
  customers: PortfolioCustomerSummary[],
): PortfolioRevenueAtRisk {
  const result: PortfolioRevenueAtRisk = {
    atRisk: 0,
    high: 0,
    total: 0,
    currency: null,
  }
  for (const customer of customers) {
    if (customer.billed12M == null) continue
    result.total += customer.billed12M
    result.currency ??= customer.primaryCurrency
    const level = customer.summary.churnRisk.level
    if (level === "high" || level === "medium") {
      result.atRisk += customer.billed12M
    }
    if (level === "high") result.high += customer.billed12M
  }
  return result
}

/** One working segment: the accounts plus the numbers that size the work. */
export type PortfolioSegment = {
  key: PortfolioSegmentKey
  /** Biggest accounts first (by seats) — where the effort pays most. */
  customers: PortfolioCustomerSummary[]
  seatCount: number
  idleSeats: number
  /** 12-month billed revenue riding on the segment's accounts. */
  billed12M: number
}

/** Engagement threshold splitting "disengaged" from "engaged" accounts. */
const SEGMENT_SCORE_SPLIT = 50
/** Utilization threshold splitting "idle-heavy" from "activated" accounts. */
const SEGMENT_UTILIZATION_SPLIT = 0.5

/**
 * Splits the book into the four working segments (see
 * PORTFOLIO_SEGMENT_LABELS). Only assessed customers segment — accounts
 * with no churn baseline are counted separately by the caller.
 */
export function derivePortfolioSegments(
  customers: PortfolioCustomerSummary[],
): PortfolioSegment[] {
  const buckets = new Map<PortfolioSegmentKey, PortfolioCustomerSummary[]>(
    PORTFOLIO_SEGMENT_ORDER.map((key) => [key, []]),
  )

  for (const customer of customers) {
    const { summary } = customer
    const level = summary.churnRisk.level
    if (level === "new" || level === "none") continue
    const atRisk = level === "high" || level === "medium"
    const engaged =
      summary.avgScore != null && summary.avgScore >= SEGMENT_SCORE_SPLIT
    const utilization =
      summary.seatCount > 0 ? summary.activeUsers28d / summary.seatCount : 0

    const key: PortfolioSegmentKey = atRisk
      ? engaged
        ? "fit-risk"
        : "activation-fires"
      : utilization < SEGMENT_UTILIZATION_SPLIT
        ? "idle-expansion"
        : "champions"
    buckets.get(key)?.push(customer)
  }

  return PORTFOLIO_SEGMENT_ORDER.map((key) => {
    const members = (buckets.get(key) ?? []).sort(
      (a, b) => b.summary.seatCount - a.summary.seatCount,
    )
    return {
      key,
      customers: members,
      seatCount: members.reduce(
        (sum, member) => sum + member.summary.seatCount,
        0,
      ),
      idleSeats: members.reduce(
        (sum, member) =>
          sum +
          Math.max(0, member.summary.seatCount - member.summary.activeUsers28d),
        0,
      ),
      billed12M: members.reduce(
        (sum, member) => sum + (member.billed12M ?? 0),
        0,
      ),
    }
  })
}

/** One row of the license-utilization list: who has seats sitting idle. */
export type PortfolioIdleLicenseTarget = {
  customer: PortfolioCustomerSummary
  activeSeats: number
  idleSeats: number
  /** Share of seats active, 0–1. */
  utilization: number
  /**
   * The idle share of the customer's 12-month billed revenue (billed ×
   * idle/seats) — an estimate of the money paying for unused seats, the
   * amount a right-sizing conversation would take off the book. Null
   * without invoice history.
   */
  idleValue12M: number | null
}

/**
 * Accounts ranked by idle licenses — the activation hit list. Idle seats
 * are unrealized value the customer pays for: the cheapest churn defense
 * and the clearest onboarding target, so the biggest piles rank first.
 */
export function deriveIdleLicenseTargets(
  customers: PortfolioCustomerSummary[],
): PortfolioIdleLicenseTarget[] {
  return customers
    .map((customer) => {
      const seats = customer.summary.seatCount
      const active = customer.summary.activeUsers28d
      const idle = Math.max(0, seats - active)
      return {
        customer,
        activeSeats: active,
        idleSeats: idle,
        utilization: seats > 0 ? active / seats : 0,
        idleValue12M:
          customer.billed12M != null && seats > 0
            ? customer.billed12M * (idle / seats)
            : null,
      }
    })
    .filter((target) => target.idleSeats > 0)
    .sort((a, b) => b.idleSeats - a.idleSeats)
}

/**
 * A customer's product-mix state over the last 28 days. v1/v2 are separate
 * products, so the four states map to sales motions: v1-only = demo v2,
 * dual = support the migration, v2-only = modern default, inactive = the
 * churn-risk view's problem, not the mix funnel's.
 */
function getPortfolioMixState(appMix: UsageAppMixEntry[]): PortfolioMixState {
  const isActive = (entry: UsageAppMixEntry) =>
    entry.weighted28d > 0 || entry.events28d > 0
  const v1 = appMix.some(
    (entry) => entry.app === "v1-console" && isActive(entry),
  )
  const v2 = appMix.some(
    (entry) => entry.app === "v2-console" && isActive(entry),
  )
  if (v1 && v2) return "dual"
  if (v1) return "v1-only"
  if (v2) return "v2-only"
  return "inactive"
}

/** Customers per product-mix state, for the migration funnel. */
export function derivePortfolioMixCounts(
  customers: PortfolioCustomerSummary[],
): Record<PortfolioMixState, number> {
  const counts: Record<PortfolioMixState, number> = {
    "v1-only": 0,
    dual: 0,
    "v2-only": 0,
    inactive: 0,
  }
  for (const customer of customers) {
    counts[getPortfolioMixState(customer.appMix)] += 1
  }
  return counts
}

/** One movers entry: a customer with its window volumes and the swing. */
export type PortfolioMover = {
  customer: PortfolioCustomerSummary
  /** Weighted 28d volume (raw events pre-rollout), current window. */
  current: number
  /** Same measure, previous window. */
  previous: number
  delta: number
}

export type PortfolioMovers = {
  rising: PortfolioMover[]
  falling: PortfolioMover[]
}

/**
 * Biggest swings in 28-day volume, both directions — the "call these
 * accounts this week" shortlists. Ranked by the absolute SIZE of the swing,
 * not the percentage: ten customers tied at -100% says nothing about where
 * the volume went, while "12,000 → 600" names the account that matters.
 * The floor applies to the reference window (previous for falling, current
 * for rising) so micro-scopes don't qualify.
 */
export function derivePortfolioMovers(
  customers: PortfolioCustomerSummary[],
  limit: number = MOVER_LIMIT,
): PortfolioMovers {
  const movers = customers.map((customer) => {
    const { summary } = customer
    // Weighted volumes when the scope has them, raw events otherwise —
    // never mixed within one customer, so the delta stays meaningful.
    const hasWeighted = summary.weightedEvents28d != null
    const current = hasWeighted
      ? (summary.weightedEvents28d ?? 0)
      : summary.events28d
    const previous = hasWeighted
      ? summary.weightedEventsPrev28d
      : summary.eventsPrev28d
    return { customer, current, previous, delta: current - previous }
  })

  const rising = movers
    .filter(
      (mover) =>
        mover.delta > 0 && mover.current >= MOVER_ACTIVITY_FLOOR_EVENTS,
    )
    .sort((a, b) => b.delta - a.delta)
    .slice(0, limit)
  const falling = movers
    .filter(
      (mover) =>
        mover.delta < 0 && mover.previous >= MOVER_ACTIVITY_FLOOR_EVENTS,
    )
    .sort((a, b) => a.delta - b.delta)
    .slice(0, limit)
  return { rising, falling }
}
